// Signed 32-bit integer bounds — the range a PostgreSQL `integer` / `integer[]` column accepts. Any value the client sends
// into such a column must fall inside this range, or the insert overflows (500). Kept here as a shared boundary helper since
// more than one field maps to an int column.
export const INT32_MIN = -2147483648;
export const INT32_MAX = 2147483647;

/** True when `value` is a finite integer within the signed-32-bit range. Rejects NaN and ±Infinity (both non-integers). */
export const isInt32 = (value: number): boolean => Number.isInteger(value) && value >= INT32_MIN && value <= INT32_MAX;

/**
 * Parse a string as a signed-32-bit integer, or `null` if it isn't one. Accepts an optional leading minus and digits only;
 * rejects blanks, non-numeric text, decimals, and anything outside the int32 range. Note a long-enough digit run makes
 * `parseInt` overflow to `Infinity` (which `isInt32` rejects) rather than throwing — so oversized input is dropped, not leaked
 * as a value that later JSON-serializes to `null` and breaks a payload.
 */
export const parseInt32 = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!/^-?\d+$/.test(trimmed)) return null;
  const parsed = parseInt(trimmed, 10);
  return isInt32(parsed) ? parsed : null;
};
